import { FC, useCallback } from 'react';
import { TypeSubFlowV2Translations } from '@/utils/translations/TypeSubFlowV2Translations';
import classNames from 'classnames';
import { variantBaseUrl } from '../utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { addToCart, removeFromCart } from '@/lib/cart.browser';
import { useSubFlowV3Data } from '../../context/SubFlowV3Context';
import { formatMoney, getFirstParagraph } from '@/utils/helpers';
import useLocale from '@/hooks/useLocale';

export interface UpsellProps {
  t: TypeSubFlowV2Translations;
}

const Upsell: FC<UpsellProps> = ({ t }) => {
  const locale = useLocale();
  const { subflow_v2_enable_standalone_upsell } = useFlags();
  const { data, api } = useSubFlowV3Data();
  const addOn =
    data.upsellAddOn ||
    data.orderReview?.standalone_add_ons.find(
      (a) => !data.standaloneAddOns.find((s) => s.variant_id === a.variant_id),
    );
  const isSelected = addOn ? api.isStandaloneAddOnSelected(addOn) : false;

  const handleAddProductToCart = async () => {
    try {
      await addToCart(
        [
          {
            merchandiseId: `${variantBaseUrl}${addOn?.variant_id}`,
            quantity: 1,
          },
        ],
        1,
        { noredirect: 1 },
      );
    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  };

  const handleRemoveProductFromCart = async () => {
    try {
      await removeFromCart([`${variantBaseUrl}${addOn?.variant_id}`], {
        noredirect: 1,
      });
    } catch (error) {
      console.error('Error removing product from cart:', error);
    }
  };

  const handleOnClick = useCallback(() => {
    if (!addOn) return;
    if (!isSelected) handleAddProductToCart();
    if (isSelected) handleRemoveProductFromCart();
    api.toggleStandaloneAddOn(addOn, true);
  }, [addOn, api]);

  if (!subflow_v2_enable_standalone_upsell || !addOn) return null;

  return (
    <div className="py-4 lg:py-0">
      <div className="text-lg font-bold lg:text-xl">{t.upsellheader}</div>
      <button
        className={classNames(
          'group relative h-full w-full rounded-lg border-4 bg-white px-4 py-3 shadow-lg lg:max-w-[400px]',
          {
            'border-transparent': !isSelected,
            'border-indigo': isSelected,
          },
        )}
        onClick={handleOnClick}
      >
        <div className="flex items-center gap-x-6">
          <div
            className="h-[89px] w-[131px] md:h-[120px] md:w-[160px] flex-none bg-contain bg-center bg-no-repeat lg:h-[140px]"
            style={{ backgroundImage: `url(${addOn.image_uri})` }}
          />
          <div className="py-2">
            <div className="text-left text-base font-bold lg:text-lg">
              {addOn.name}
            </div>
            <div className="text-left text-sm lg:text-base">
              {getFirstParagraph(addOn.description_html)}
            </div>
          </div>
        </div>
        <div className="flex justify-between border-t pt-3">
          <div className="flex items-center">
            <div className="relative leading-none">
              <input
                checked={isSelected}
                className="peer block block h-[24px] w-[24px] cursor-pointer appearance-none rounded-sm border border-indigo checked:!bg-indigo checked:focus:outline-none group-focus-visible:bg-[#80bcfe99] group-focus-visible:outline group-focus-visible:outline-[0.1875rem] group-focus-visible:outline-offset-0 group-focus-visible:outline-[#80bcfe99]"
                onChange={handleOnClick}
                type="checkbox"
                tabIndex={-1}
              />
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute left-2/4 top-2/4 hidden -translate-x-2/4 -translate-y-2/4 text-white peer-checked:block"
              >
                <path
                  d="M20 6L9 17L4 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="ml-2">{t.startingKitAddOrder}</div>
          </div>
          <div className="text-base font-bold">
            {formatMoney(addOn.price / 100, locale, true)}
          </div>
        </div>
      </button>
    </div>
  );
};

export default Upsell;
